import { QueryHookOptions, useQuery, QueryResult } from '@apollo/client'
import { DocumentNode } from 'graphql'
import { useCallback, useMemo, useState } from 'react'
import { Direction, PropertySortInput } from 'src/graphql-types/globalTournamentTypes'
import { retrieveRowsPerPage } from 'src/utils/storage/local-storage'
import { SortDirectionEnum } from 'src/graphql-types/globalRankingTypes'

export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50]
export const ROWS_PER_PAGE_OPTIONS_EXPANDED = [10, 25, 50, 100]

export interface PaginationVariables {
  limit: number
  offset: number
}

export interface SortVariables {
  orderBy: string
  order: TableSortOrder
}

export interface ControlProps {
  onControlChange: (controls: { pagination?: PaginationVariables; sorting?: SortVariables }) => void
  totalItems?: number
  sortDisabled?: boolean
  paginaitonDisabled?: boolean
  dataSetKey?: string
}

interface QueryResultWithControls<TData, TVariables> extends QueryResult<TData, TVariables> {
  controlProps: ControlProps
  queryParams: { paging: PaginationVariables; sorting?: PropertySortInput[] }
}

export interface QueryHookOptionsWithPagination<TData, TVariables, TDatum>
  extends QueryHookOptions<TData, Omit<TVariables, 'offset' | 'limit' | 'skip'>> {
  getTotalItems: (d: TData) => number
  getSortProperty?: (key: string) => string
  transformVariables?: (options: any) => any
}

export function useControlledQuery<
  TData = any,
  TVariables extends PaginationVariables = PaginationVariables,
  TDatum = any
>(
  query: DocumentNode,
  options: QueryHookOptionsWithPagination<TData, TVariables, TDatum>
): QueryResultWithControls<TData, TVariables> {
  const storedRowsPerPage = useMemo(() => retrieveRowsPerPage(), [])
  const [pageParams, setPageParams] = useState({
    limit: storedRowsPerPage ?? ROWS_PER_PAGE_OPTIONS[0],
    offset: 0
  })

  const transformVariables = options.transformVariables

  const [sorts, setSorts] = useState<PropertySortInput[]>()
  const variables = options.variables

  const variablesWithControls = useMemo(() => {
    const vars = { ...(variables || {}), ...pageParams }
    if (sorts) vars['sorts'] = sorts
    return transformVariables ? transformVariables(vars) : vars
  }, [variables, pageParams, sorts, transformVariables])

  const optionsWithControls = useMemo(() => {
    return { ...options, variables: variablesWithControls }
  }, [options, variablesWithControls])

  const queryResult = useQuery<TData, TVariables>(query, optionsWithControls)
  const data = queryResult.data

  const getTotalItems = options?.getTotalItems
  const totalItems = useMemo<number>(() => (data && getTotalItems ? getTotalItems(data) : 0), [
    data,
    getTotalItems
  ])

  const getSortProperty = options?.getSortProperty

  const onControlChange = useCallback<ControlProps['onControlChange']>(
    p => {
      const { pagination, sorting } = p
      if (pagination) {
        setPageParams(pagination)
      }
      if (sorting) {
        setSorts([
          {
            sortDirection: sorting.order,
            property: getSortProperty?.(sorting.orderBy) ?? `${sorting.orderBy}`
          }
        ])
      }
    },
    [setPageParams, setSorts, getSortProperty]
  )
  const controlProps = useMemo<ControlProps>(() => ({ onControlChange, totalItems }), [
    onControlChange,
    totalItems
  ])
  return useMemo(
    () => ({ ...queryResult, controlProps, queryParams: { paging: pageParams, sorting: sorts } }),
    [queryResult, controlProps, pageParams, sorts]
  )
}

export interface TableSortFunction<T> {
  (orderBy: keyof T, order: TableSortOrder): void
}

export type TableSortOrder = Direction

export const localSort = (data, orderBy, order) => {
  return stableSort(data, getComparator(order, orderBy))
}

const stableSort = (array: any[], comparator: (a: any, b: any) => number) => {
  const stabilizedThis = array.map((el, index) => [el, index] as [any, number])

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map(el => el[0])
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator<T extends keyof any>(
  order: TableSortOrder,
  orderBy: T
): (a: { [key in T]: number | string }, b: { [key in T]: number | string }) => number {
  return order === Direction.DESCENDING
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

// export function useLocalPagination<T>(data: T[]): PaginationProps & { dataPage: T[] } {
//   const [dataPage, setDataPage] = useState(data)
//   const fetchData = useCallback<PaginationProps['fetchData']>(
//     (offset, limit) => {
//       setDataPage(data.slice(offset, offset + limit))
//     },
//     [data, setDataPage]
//   )
//   const pagination = useMemo(() => ({ fetchData, totalItems: data.length }), [fetchData, data])
//   return { ...pagination, dataPage }
// }

function getLocalPage(data: any[], offset: number, limit: number) {
  return data.slice(offset, offset + limit)
}

export function transformSortDirection(direction: Direction): SortDirectionEnum {
  if (direction === Direction.DESCENDING) return SortDirectionEnum.DESC
  if (direction === Direction.ASCENDING) return SortDirectionEnum.ASC
  return direction
}
